<script lang="ts" setup>
import { computed, ref, watch } from 'vue';

export type QuizAlertType = 'success' | 'error' | 'warning' | 'info';

interface Props {
  type?: QuizAlertType;
  message: string;
  show?: boolean;
  dismissible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  show: true,
  dismissible: true,
});

const emit = defineEmits(['close']);

const isVisible = ref(props.show);

watch(() => props.show, (newValue) => {
  isVisible.value = newValue;
});

const baseClasses = 'p-4 rounded-md border flex items-start';

const typeClasses = computed(() => {
  switch (props.type) {
    case 'success':
      return 'bg-green-50 border-green-300 text-green-800';
    case 'error':
      return 'bg-red-50 border-red-300 text-red-800';
    case 'warning':
      return 'bg-yellow-50 border-yellow-300 text-yellow-800';
    case 'info':
    default:
      return 'bg-blue-50 border-blue-300 text-blue-800';
  }
});

const icon = computed(() => {
  switch (props.type) {
    case 'success':
      return '✓';
    case 'error':
      return '✕';
    case 'warning':
      return '!';
    case 'info':
    default:
      return 'ℹ';
  }
});

const iconClasses = computed(() => {
  switch (props.type) {
    case 'success':
      return 'text-green-500';
    case 'error':
      return 'text-red-500';
    case 'warning':
      return 'text-yellow-500';
    case 'info':
    default:
      return 'text-blue-500';
  }
});

function closeAlert() {
  isVisible.value = false;
  emit('close');
}
</script>

<template>
  <div v-if="isVisible" :class="[baseClasses, typeClasses]" role="alert">
    <span :class="['font-bold mr-3', iconClasses]">{{ icon }}</span>

    <div class="flex-grow">
      {{ message }}
    </div>

    <button v-if="dismissible" @click="closeAlert"
      :class="['ml-4 -mt-1 -mr-1 p-1 rounded-md focus:outline-none focus:ring-2', iconClasses]"
      :aria-label="`Close ${type} alert`">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"
        stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>